import { useEffect, useMemo, useRef, useState } from 'react';
import type { EncodedResponse } from '../../data/types';
import { cn } from '../../lib/cn';
import { useMotionPreferences } from '../preferences';
import { capDevicePixelRatio, createParticleData, resolveLayout } from './layouts';
import type { ParticleLayoutName, ParticlePosition, ParticleTarget } from './types';

interface ParticleFieldProps {
  rows: EncodedResponse[];
  layout?: ParticleLayoutName;
  matchedIds?: ReadonlySet<number>;
  bandField?: keyof EncodedResponse;
  className?: string;
  onSettled?: () => void;
  radiusScale?: number;
}

const PRIMARY = '#1a60a2';
const SLATE = '#64748b';

function toPixelTarget(target: ParticleTarget, width: number, height: number): ParticleTarget {
  return { ...target, x: target.x * width, y: target.y * height };
}

export function ParticleField({
  rows,
  layout = 'field',
  matchedIds,
  bandField,
  className,
  onSettled,
  radiusScale = 1,
}: ParticleFieldProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<Map<number, ParticlePosition>>(new Map());
  const settledRef = useRef(false);
  const transitionRef = useRef(0);
  const onSettledRef = useRef(onSettled);
  const matchedIdsRef = useRef(matchedIds);
  onSettledRef.current = onSettled;
  matchedIdsRef.current = matchedIds;
  const [size, setSize] = useState({ width: 1280, height: 720 });
  const [settled, setSettled] = useState(false);
  const [settledTargets, setSettledTargets] = useState<ParticleTarget[]>([]);
  const [canvasUnavailable, setCanvasUnavailable] = useState(false);
  const [visible, setVisible] = useState(true);
  const { reducedMotion } = useMotionPreferences();
  const particles = useMemo(() => createParticleData(rows), [rows]);
  const normalizedTargets = useMemo(
    () => resolveLayout(layout, particles, { ...size, bandField }),
    [bandField, layout, particles, size],
  );
  const targets = useMemo(
    () => normalizedTargets.map((target) => {
      const pixelTarget = toPixelTarget(target, size.width, size.height);
      const responsiveRadiusScale = size.width < 768 ? Math.min(radiusScale, 1) : radiusScale;
      return { ...pixelTarget, radius: pixelTarget.radius * responsiveRadiusScale };
    }),
    [normalizedTargets, radiusScale, size],
  );

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry?.isIntersecting ?? true));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || targets.length === 0) return;
    const transition = ++transitionRef.current;
    settledRef.current = false;
    setSettled(false);
    const context = canvas.getContext('2d');
    if (!context) {
      setCanvasUnavailable(true);
      setSettledTargets(targets);
      settledRef.current = true;
      setSettled(true);
      return;
    }
    const drawingContext = context;

    const dpr = capDevicePixelRatio(window.devicePixelRatio);
    canvas.width = Math.round(size.width * dpr);
    canvas.height = Math.round(size.height * dpr);
    canvas.style.width = `${size.width}px`;
    canvas.style.height = `${size.height}px`;
    drawingContext.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (reducedMotion) {
      const positions = new Map<number, ParticlePosition>();
      for (const target of targets) positions.set(target.id, { ...target, vx: 0, vy: 0 });
      positionsRef.current = positions;
      setSettledTargets(targets);
      settledRef.current = true;
      setSettled(true);
      onSettledRef.current?.();
      return;
    }

    const targetMap = new Map(targets.map((target) => [target.id, target]));
    for (const target of targets) {
      if (!positionsRef.current.has(target.id)) {
        positionsRef.current.set(target.id, {
          ...target,
          x: size.width * 0.5,
          y: size.height * 0.52,
          vx: 0,
          vy: 0,
        });
      }
    }

    let frame = 0;
    let lastTime = performance.now();
    let slowFrames = 0;
    let cancelled = false;
    let documentVisible = !document.hidden;
    const onVisibility = () => {
      documentVisible = !document.hidden;
      if (documentVisible && !cancelled && !frame && !settledRef.current) frame = requestAnimationFrame(tick);
    };
    document.addEventListener('visibilitychange', onVisibility);

    function draw() {
      drawingContext.clearRect(0, 0, size.width, size.height);
      for (const position of positionsRef.current.values()) {
        const currentMatches = matchedIdsRef.current;
        const matched = currentMatches === undefined || currentMatches.has(position.id);
        drawingContext.globalAlpha = matched ? 0.72 : 0.14;
        drawingContext.fillStyle = matched ? PRIMARY : SLATE;
        drawingContext.beginPath();
        drawingContext.arc(position.x, position.y, position.radius, 0, Math.PI * 2);
        drawingContext.fill();
      }
      drawingContext.globalAlpha = 1;
    }

    function tick(now: number) {
      frame = 0;
      if (cancelled || !visible || !documentVisible) return;
      const elapsed = now - lastTime;
      slowFrames = elapsed > 45 ? slowFrames + 1 : Math.max(0, slowFrames - 1);
      if (slowFrames >= 6) {
        const positions = new Map<number, ParticlePosition>();
        for (const target of targets) positions.set(target.id, { ...target, vx: 0, vy: 0 });
        positionsRef.current = positions;
        setSettledTargets(targets);
        settledRef.current = true;
        setSettled(true);
        onSettledRef.current?.();
        return;
      }
      const dt = Math.min(elapsed / 1000, 0.033);
      lastTime = now;
      let moving = false;
      for (const position of positionsRef.current.values()) {
        const target = targetMap.get(position.id);
        if (!target) continue;
        const accelerationX = (target.x - position.x) * 68 - position.vx * 16;
        const accelerationY = (target.y - position.y) * 68 - position.vy * 16;
        position.vx += accelerationX * dt;
        position.vy += accelerationY * dt;
        position.x += position.vx * dt;
        position.y += position.vy * dt;
        position.radius = target.radius;
        if (
          Math.abs(target.x - position.x) > 0.15 ||
          Math.abs(target.y - position.y) > 0.15 ||
          Math.abs(position.vx) > 0.12 ||
          Math.abs(position.vy) > 0.12
        ) {
          moving = true;
        }
      }
      draw();
      if (moving) {
        frame = requestAnimationFrame(tick);
      } else {
        for (const position of positionsRef.current.values()) {
          const target = targetMap.get(position.id);
          if (target) Object.assign(position, target, { vx: 0, vy: 0 });
        }
        draw();
        if (transition === transitionRef.current) {
          setSettledTargets(targets);
          settledRef.current = true;
          setSettled(true);
          onSettledRef.current?.();
        }
      }
    }

    if (visible && documentVisible) frame = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [reducedMotion, size, targets, visible]);

  const showSvg = reducedMotion || canvasUnavailable || settled;
  const svgTargets = settledTargets.length > 0 ? settledTargets : targets;

  return (
    <div
      ref={wrapperRef}
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
      aria-hidden="true"
    >
      <canvas
        ref={canvasRef}
        className={cn(
          'absolute inset-0 transition-opacity duration-300',
          showSvg ? 'opacity-0' : 'opacity-100',
        )}
      />
      <svg
        viewBox={`0 0 ${size.width} ${size.height}`}
        className={cn(
          'absolute inset-0 h-full w-full transition-opacity duration-300',
          showSvg ? 'opacity-100' : 'opacity-0',
        )}
        preserveAspectRatio="none"
      >
          {svgTargets.map((target) => {
            const matched = matchedIds === undefined || matchedIds.has(target.id);
            return (
              <circle
                key={target.id}
                cx={target.x}
                cy={target.y}
                r={target.radius}
                fill={matched ? PRIMARY : SLATE}
                opacity={matched ? 0.72 : 0.14}
              />
            );
          })}
      </svg>
    </div>
  );
}
